import { combineReducers, Reducer } from 'redux';

import user, { IState as IUserState } from './user/reducer';
import securityProfiles, { IState as ISecurityProfilesState } from './securityProfiles';
import lookups, { IState as ILookupsState } from './lookups';
import tags, { IState as ITagsState } from './tags';
import product, { IState as IProductState } from './product';
import settings, { IState as ISettingsState } from './settings';
import search, { IState as ISearchState } from './search';
import upload, { IState as IUploadState } from './upload';
import { publishSettings, IState as IPublishState } from './publishEndpoints';
import publishSelectedAssetsToEndPoints, { IState as ISelectedAssets } from './publishAssets';
import deleteAssets, { IState as IDeleteAssets } from './deleteAssets';
import filterAssets, { IState as IFilterAssets } from './filterAssets';
import liveAssetModal, { IState as ILiveAssetModalState } from './liveEvents/liveAssetModal';
import shareDialog, { IState as IShareDialogState } from './shareDialog';
import shareEmailDialog, { IState as IShareEmailDialogState } from './shareEmailDialog';
import supportEmailDialog, { IState as ISupportEmailDialogState } from './supportEmailDialog';
import jobStatus, { IState as IJobStatusState } from './jobStatus';
import liveEvents, { IState as ILiveEventsState } from './liveEvents/liveEvents';
import liveEventsList, { IState as ILiveEventsListState } from './liveEvents/liveEventsList';
import systemSettings, { IState as ISystemSettingsState } from './systemSettings';
import localization, { IState as ILocalizationState } from './localization';

import clipperButtons, { IState as IButtonsState } from '../components/VirtualClipper/state/buttons';
import videoPlayer, { IState as IVideoPlayerState } from '../components/VirtualClipper/state/videoPlayer';
import videoContent from '../components/VirtualClipper/state/videoContent';
import currentEditingClip, { IState as ICurrentEditingClipState } from '../components/VirtualClipper/state/currentEditingClip';
import currentPlayingClip, { IState as ICurrentPlayingClipState } from '../components/VirtualClipper/state/currentPlayingClip';
import currentReviewingClip, { IState as ICurrentReviewingClipState } from '../components/VirtualClipper/state/currentReviewingClip';
import clipperMediaContent, { IState as IClipperMediaContentState } from '../components/VirtualClipper/state/clipperMediaContent';
import clipList, { IState as IClipListState } from '../components/VirtualClipper/state/clipList';
import createDownloadDialog, { IState as ICreateDownloadDialogState } from '../components/VirtualClipper/state/createDownloadDialog';
import prePostRollSelection, { IState as IPrePostRollSelectionState } from '../components/VirtualClipper/state/prePostRollSelection';
import selectedClipsPreview, { IState as ISelectedClipsPreviewState } from './selectedClipsPreview';
import exchange, { IState as IExchangeState } from './exchange/reducer';
import validation, { IState as IValidationState } from './validation';

import metatags from './metatags';
import rawAssetDetails from './rawAssetDetails';
import previewAsset from './previewAsset';
import videoSources from '../components/VirtualClipper/state/videoSources';
import editMetadata from './editMetadata';
import warningDialog from '../components/AssetDetails/state/warningDialog';
import assetAccess, { IState as IAssetAccessState } from './assetAccess';
import assetPublishes, { IState as IAssetPublishState } from './assetPublishes';
import assetShares, { IState as IAssetSharesState } from './assetShares';
import assetPushToCloud, { IState as IAssetPushToCloudState } from './assetPushToCloud';
import bulkEditAssetsDialog, { IState as IBulkEditAssets } from './bulkUpdateAssets';
import vaultUploadDialog, { IState as IVaultUploadState } from './vaultUploadDialog';

import { IState as IEditMetadata } from '../components/AssetDetails/interfaces/editMetadata';
import { IState as IWarningDialog } from '../components/AssetDetails/interfaces/warningDialog';
import adminDashboard, { IState as IAdminDashboardState } from './adminDashboard';
import notifications, { IState as INotificationsState } from './notifications';
import storageSummary, { IState as IStorageSummaryState } from './storageSummary';
import timeoutTeams, { IState as ITimeoutTeamsState } from './timeoutTeams';
import selectedAssetRenditions, { IState as ISelectedAssetRenditionsState } from './selectedAssetRenditions';
import jobsLiveEvents, { IState as IJobsLiveEventsState } from './liveEvents/jobsLiveEvents';

export interface IAppState {
  user: IUserState;
  securityProfiles: ISecurityProfilesState;
  lookups: ILookupsState;
  tags: ITagsState;
  product: IProductState,
  settings: ISettingsState,
  search: ISearchState;
  upload: IUploadState;
  publishSettings: IPublishState;
  jobStatus: IJobStatusState;
  publishSelectedAssetsToEndPoints: ISelectedAssets;
  filterAssets: IFilterAssets;
  assetAccess: IAssetAccessState;
  assetPublishes: IAssetPublishState;
  assetShares: IAssetSharesState;
  assetPushToCloud: IAssetPushToCloudState;
  editMetadata: IEditMetadata;
  warningDialog: IWarningDialog;
  deleteAssets: IDeleteAssets;
  bulkEditAssetsDialog: IBulkEditAssets,
  createDownloadDialog: ICreateDownloadDialogState;
  prePostRollSelection: IPrePostRollSelectionState;
  selectedClipsPreview: ISelectedClipsPreviewState;
  shareDialog: IShareDialogState;
  shareEmailDialog: IShareEmailDialogState;
  supportEmailDialog: ISupportEmailDialogState;
  validation: IValidationState;
  metatags: any;
  vaultUploadDialog: IVaultUploadState;
  adminDashboard: IAdminDashboardState;
  systemSettings: ISystemSettingsState,
  localization: ILocalizationState,
  notifications: INotificationsState,
  storageSummary: IStorageSummaryState,
  clipList: IClipListState,
  videoPlayer: IVideoPlayerState,
  currentPlayingClip: ICurrentPlayingClipState,
  currentReviewingClip: ICurrentReviewingClipState,
  currentEditingClip: ICurrentEditingClipState,
  clipperButtons: IButtonsState,
  timeoutTeams: ITimeoutTeamsState,
  clipperMediaContent: IClipperMediaContentState,
  rawAssetDetails: any; // TODO add typing
  previewAsset: any; // TODO add typing
  selectedAssetRenditions: ISelectedAssetRenditionsState;
  // Live events
  liveEventsList: ILiveEventsListState;
  liveEvents: ILiveEventsState;
  liveAssetModal: ILiveAssetModalState;
  jobsLiveEvents: IJobsLiveEventsState;
  // Exchange
  exchange: IExchangeState;
}

const reducers = combineReducers({
  user,
  securityProfiles,
  lookups,
  tags,
  product,
  settings,
  search,
  upload,
  jobStatus,
  exchange,
  validation,
  systemSettings,
  localization,
  notifications,
  // Publish reducers
  publishSettings,
  publishSelectedAssetsToEndPoints,
  // filter assets
  filterAssets,
  // virtual clipper reducers
  clipperButtons,
  videoPlayer,
  videoContent,
  currentEditingClip,
  currentPlayingClip,
  currentReviewingClip,
  clipList,
  createDownloadDialog,
  prePostRollSelection,
  selectedClipsPreview,
  clipperMediaContent,

  adminDashboard,

  // asset details panel reducer
  metatags,
  rawAssetDetails,
  previewAsset,
  videoSources,
  editMetadata,
  warningDialog,
  deleteAssets,
  assetAccess,
  assetPublishes,
  assetShares,
  assetPushToCloud,
  bulkEditAssetsDialog,

  // modals,
  shareDialog,
  shareEmailDialog,
  supportEmailDialog,
  vaultUploadDialog,

  // Live Events reducers
  liveEvents,
  liveEventsList,
  liveAssetModal,
  jobsLiveEvents,

  // storage summary
  storageSummary,
  // timeout
  timeoutTeams,
  selectedAssetRenditions,
});

export default reducers;
